import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { auth } from '../../../lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Progress } from '../../ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';
import AIVoiceMetrics from '../Analytics/AIVoiceMetrics';
import { tenantHeaders } from '../../../lib/tenantSession';
import {
  Database, Activity, RefreshCw, Download, Upload, Filter, Search,
  CheckCircle, AlertTriangle, Clock, TrendingUp, Bot, Brain
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Live tenant data domains. Each is a REAL collection scoped to the tenant; we read
// real records + counts from existing tenant-scoped endpoints. No mock data.
const DOMAINS = [
  { key: 'conversations', name: 'Conversations', type: 'Conversation history',
    url: (bid) => `${API}/api/v1/conversations/business/${bid}`, listKey: 'conversations', channels: ['Web', 'Voice', 'AI'] },
  { key: 'tickets', name: 'Tickets', type: 'Support tickets',
    url: (bid) => `${API}/api/v1/tickets/business/${bid}`, listKey: 'tickets', channels: ['Web', 'Email', 'Phone'] },
  { key: 'contacts', name: 'Contacts', type: 'Customer contacts',
    url: () => `${API}/api/v1/contacts`, listKey: null, channels: ['CRM'] },
  { key: 'surveys', name: 'Surveys', type: 'Feedback surveys',
    url: (bid) => `${API}/api/v1/surveys/business/${bid}`, listKey: 'surveys', channels: ['Web', 'Email'] },
];

const getToken = async () =>
  (auth.currentUser ? await auth.currentUser.getIdToken() : null) || localStorage.getItem('crm_access_token') || '';

const fmtCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n));
const since = (records) => {
  const ts = records.map(r => r.updated_at || r.created_at || r.last_message_at).filter(Boolean).sort();
  if (!ts.length) return null;
  return ts[ts.length - 1];
};
const ago = (iso) => {
  if (!iso) return 'never';
  const d = (Date.now() - new Date(iso).getTime()) / 60000;
  if (isNaN(d)) return 'unknown';
  if (d < 1) return 'just now';
  if (d < 60) return `${Math.round(d)} min ago`;
  if (d < 1440) return `${Math.round(d / 60)} h ago`;
  return `${Math.round(d / 1440)} d ago`;
};

// Derive a REAL completeness/health score per domain from its records.
const healthOf = (key, records) => {
  if (!records.length) return 0;
  let ok = 0;
  for (const r of records) {
    if (key === 'contacts') ok += (r.email || r.phone || r.phone_number) ? 1 : 0;
    else if (key === 'tickets') ok += (r.status && (r.title || r.subject || r.description)) ? 1 : 0;
    else if (key === 'conversations') ok += (r.status && (r.customer_id || r.customer_name)) ? 1 : 0;
    else if (key === 'surveys') ok += (r.title || r.name) ? 1 : 0;
    else ok += 1;
  }
  return Math.round((ok / records.length) * 1000) / 10;
};

export const toCSV = (rows) => {
  if (!rows.length) return '';
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r || {}).forEach(k => s.add(k)); return s; }, new Set()));
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
};
const download = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

const DataHub = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { currentBusiness } = useBusiness();
  const brandId = currentBusiness?.brandId;
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState([]);           // [{key,name,type,records,count,health,lastSync,channels}]
  const [selectedKey, setSelectedKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataFiltersOpen, setDataFiltersOpen] = useState(false);
  const [exportDetailsOpen, setExportDetailsOpen] = useState(false);
  const [filters, setFilters] = useState({ dataType: 'all', status: 'all', source: 'all' });
  const [syncing, setSyncing] = useState({});           // {key: true}
  const [importing, setImporting] = useState(false);

  const fetchDomain = useCallback(async (domain) => {
    const token = await getToken();
    const res = await fetch(domain.url(brandId), { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } });
    if (!res.ok) throw new Error(`${domain.name} (${res.status})`);
    const data = await res.json();
    const records = domain.listKey ? (data[domain.listKey] || []) : (Array.isArray(data) ? data : []);
    return {
      key: domain.key, name: domain.name, type: domain.type, channels: domain.channels,
      records, count: records.length, health: healthOf(domain.key, records), lastSync: since(records),
    };
  }, [brandId]);

  const loadAll = useCallback(async () => {
    if (!brandId) { setLoading(false); return; }
    setLoading(true);
    const results = await Promise.all(DOMAINS.map(async (d) => {
      try { return await fetchDomain(d); }
      catch { return { key: d.key, name: d.name, type: d.type, channels: d.channels, records: [], count: 0, health: 0, lastSync: null, error: true }; }
    }));
    setSources(results);
    setLoading(false);
  }, [brandId, fetchDomain]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const syncOne = async (key) => {
    const domain = DOMAINS.find(d => d.key === key);
    setSyncing(p => ({ ...p, [key]: true }));
    try {
      const fresh = await fetchDomain(domain);
      setSources(prev => prev.map(s => s.key === key ? fresh : s));
      toast({ title: t('rd.synced'), description: t('rd.syncedDesc', { name: t(`rd.domain.${domain.key}.name`), count: fresh.count }) });
    } catch (e) {
      toast({ title: t('rd.syncFailed'), description: e.message, variant: 'destructive' });
    } finally { setSyncing(p => ({ ...p, [key]: false })); }
  };

  const syncAll = async () => {
    toast({ title: t('rd.syncing'), description: t('rd.syncingDesc') });
    await loadAll();
    toast({ title: t('rd.syncComplete'), description: t('rd.syncCompleteDesc') });
  };

  const exportSource = (key, format = 'csv') => {
    const s = sources.find(x => x.key === key);
    if (!s || !s.records.length) { toast({ title: t('rd.nothingToExport'), description: t('rd.noRecordsDesc', { name: s ? t(`rd.domain.${s.key}.name`) : t('rd.source') }), variant: 'destructive' }); return; }
    const stamp = new Date().toISOString().slice(0, 10);
    if (format === 'json') download(JSON.stringify(s.records, null, 2), `${s.key}_${stamp}.json`, 'application/json');
    else download(toCSV(s.records), `${s.key}_${stamp}.csv`, 'text/csv');
    toast({ title: t('rd.exportReady'), description: t('rd.exportReadyDesc', { name: t(`rd.domain.${s.key}.name`), count: s.records.length, format: format.toUpperCase() }) });
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) throw new Error(t('rd.csvHeaderError'));
      const header = lines[0].split(',').map(h => h.trim());
      const contacts = lines.slice(1).map(line => {
        const cells = line.split(',');
        const obj = {};
        header.forEach((h, i) => { obj[h] = (cells[i] || '').trim(); });
        return obj;
      });
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/contacts/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify({ contacts, source: 'data_hub_import' }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || t('rd.importFailedStatus', { status: res.status }));
      toast({ title: t('rd.importComplete'), description: t('rd.importCompleteDesc', { imported: d.successful_imports ?? 0, duplicates: d.duplicate_contacts ?? 0, failed: d.failed_imports ?? 0 }) });
      await syncOne('contacts');
    } catch (err) {
      toast({ title: t('rd.importFailed'), description: err.message, variant: 'destructive' });
    } finally { setImporting(false); }
  };

  const visible = sources
    .filter(s => filters.source === 'all' || s.key === filters.source)
    .filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()) || s.type.toLowerCase().includes(searchTerm.toLowerCase()));

  const totalRecords = sources.reduce((sum, s) => sum + s.count, 0);
  const withData = sources.filter(s => s.count > 0).length;
  const avgHealth = sources.length ? (sources.reduce((sum, s) => sum + s.health, 0) / sources.length) : 0;
  const selected = sources.find(s => s.key === selectedKey);

  const getHealthColor = (h) => (h >= 95 ? 'text-green-600' : h >= 80 ? 'text-yellow-600' : 'text-red-600');

  if (!brandId) return (
    <div className="flex-1 p-6"><Card><CardContent className="py-10 text-center text-gray-600">
      {t('rd.noWorkspace')}
    </CardContent></Card></div>
  );

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImportFile} />
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Brain className="h-6 w-6 text-purple-600" />{t('nav.dataHub')}
            </h1>
            <p className="text-gray-600">{t('rd.liveTenantData')} — {currentBusiness?.businessName || brandId}</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing}>
              <Upload className="h-4 w-4 mr-2" />{importing ? t('rd.importing') : t('rd.importData')}
            </Button>

            <Dialog open={dataFiltersOpen} onOpenChange={setDataFiltersOpen}>
              <DialogTrigger asChild>
                <Button variant="outline"><Filter className="h-4 w-4 mr-2" />{t('rd.dataFilters')}</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('rd.filterDataSources')}</DialogTitle>
                  <DialogDescription>{t('rd.filterDataSourcesDesc')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">{t('rd.dataSource')}</label>
                    <Select value={filters.source} onValueChange={(v) => setFilters(p => ({ ...p, source: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('rd.allSources')}</SelectItem>
                        {sources.map(s => <SelectItem key={s.key} value={s.key}>{t(`rd.domain.${s.key}.name`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-2 pt-2">
                    <Button variant="outline" onClick={() => { setFilters({ dataType: 'all', status: 'all', source: 'all' }); }}>{t('rd.clear')}</Button>
                    <Button onClick={() => setDataFiltersOpen(false)}>{t('rd.apply')}</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button onClick={syncAll} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{t('rd.syncAll')}
            </Button>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input placeholder={t('rd.searchDataSources')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 max-w-md" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card><CardContent className="p-4 text-center">
            <Database className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-gray-900">{loading ? '…' : sources.length}</div>
            <div className="text-xs text-gray-600">{t('rd.dataSources')}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-green-700">{loading ? '…' : withData}</div>
            <div className="text-xs text-gray-600">{t('rd.withData')}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <Activity className="h-6 w-6 text-purple-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-purple-700">{loading ? '…' : fmtCount(totalRecords)}</div>
            <div className="text-xs text-gray-600">{t('rd.totalRecords')}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <TrendingUp className="h-6 w-6 text-orange-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-orange-700">{loading ? '…' : `${avgHealth.toFixed(1)}%`}</div>
            <div className="text-xs text-gray-600">{t('rd.avgCompleteness')}</div>
          </CardContent></Card>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Bot className="h-6 w-6 text-blue-600" />{t('rd.aiVoiceAgentPerformance')}</h2>
        <AIVoiceMetrics compact={true} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader><CardTitle>{t('rd.dataSources')}</CardTitle></CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12 text-gray-500">{t('rd.loadingTenantData')}</div>
              ) : visible.length === 0 ? (
                <div className="text-center py-12 text-gray-500">{t('rd.noDataSourcesMatch')}</div>
              ) : (
                <div className="space-y-4">
                  {visible.map((source) => (
                    <div key={source.key}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${selectedKey === source.key ? 'border-purple-200 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}
                      onClick={() => setSelectedKey(source.key)}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <Database className="h-5 w-5 text-gray-500" />
                          <div><h3 className="font-medium text-gray-900">{t(`rd.domain.${source.key}.name`)}</h3><p className="text-sm text-gray-500">{t(`rd.domain.${source.key}.type`)}</p></div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {source.error && <AlertTriangle className="h-4 w-4 text-red-500" title={t('rd.failedToLoad')} />}
                          <Badge className={source.count > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {source.error ? t('rd.badgeError') : source.count > 0 ? t('rd.badgeLive') : t('rd.badgeEmpty')}
                          </Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div><span className="text-gray-500">{t('rd.recordsLabel')}</span><span className="font-medium ml-1">{fmtCount(source.count)}</span></div>
                        <div><span className="text-gray-500">{t('rd.completenessLabel')}</span><span className={`font-medium ml-1 ${getHealthColor(source.health)}`}>{source.health}%</span></div>
                        <div className="flex items-center"><Clock className="h-3 w-3 text-gray-400 mr-1" /><span className="text-gray-500">{t('rd.latestLabel')}</span><span className="font-medium ml-1">{ago(source.lastSync)}</span></div>
                      </div>
                      <div className="flex justify-between items-center mt-3">
                        <div className="flex space-x-1">{source.channels?.map((c, i) => <Badge key={i} variant="secondary" className="text-xs">{c}</Badge>)}</div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); syncOne(source.key); }} disabled={!!syncing[source.key]}>
                            <RefreshCw className={`h-3 w-3 mr-1 ${syncing[source.key] ? 'animate-spin' : ''}`} />{t('rd.sync')}
                          </Button>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); exportSource(source.key, 'csv'); }} disabled={!source.count}>
                            <Download className="h-3 w-3 mr-1" />{t('rd.export')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader><CardTitle>{t('rd.dataQuality')}</CardTitle></CardHeader>
            <CardContent>
              {loading ? <div className="text-center py-8 text-gray-500">{t('common.loading')}</div> : (
                <div className="space-y-4">
                  {sources.map((s) => (
                    <div className="space-y-2" key={s.key}>
                      <div className="flex justify-between text-sm"><span>{t(`rd.domain.${s.key}.name`)} {t('rd.completeness')}</span><span className="font-medium">{s.count ? `${s.health}%` : t('common.noData')}</span></div>
                      <Progress value={s.count ? s.health : 0} className="w-full" />
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 pt-2">{t('rd.completenessNote')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {selected && (
        <div className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2"><Database className="h-5 w-5" /><span>{t(`rd.domain.${selected.key}.name`)} {t('rd.details')}</span></CardTitle>
                <Dialog open={exportDetailsOpen} onOpenChange={setExportDetailsOpen}>
                  <DialogTrigger asChild><Button variant="outline" disabled={!selected.count}><Download className="h-4 w-4 mr-2" />{t('rd.exportDetails')}</Button></DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>{t('rd.export')} {t(`rd.domain.${selected.key}.name`)}</DialogTitle><DialogDescription>{t('rd.downloadRecords', { count: selected.count })}</DialogDescription></DialogHeader>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <Button variant="outline" className="h-16 flex flex-col" onClick={() => { exportSource(selected.key, 'json'); setExportDetailsOpen(false); }}>
                        <Database className="h-6 w-6 mb-1" /><span className="text-xs">JSON</span>
                      </Button>
                      <Button variant="outline" className="h-16 flex flex-col" onClick={() => { exportSource(selected.key, 'csv'); setExportDetailsOpen(false); }}>
                        <Download className="h-6 w-6 mb-1" /><span className="text-xs">CSV</span>
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">{t('nav.overview')}</h4>
                  <div className="space-y-2">
                    <div><span className="text-gray-500">{t('rd.recordsLabel')}</span> {fmtCount(selected.count)}</div>
                    <div><span className="text-gray-500">{t('rd.completenessLabel')}</span> <span className={getHealthColor(selected.health)}>{selected.health}%</span></div>
                    <div><span className="text-gray-500">{t('rd.latestActivity')}</span> {ago(selected.lastSync)}</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">{t('rd.channels')}</h4>
                  <div className="flex flex-wrap gap-1">{selected.channels?.map((c, i) => <Badge key={i} variant="secondary" className="text-xs">{c}</Badge>)}</div>
                  <Button size="sm" variant="outline" className="mt-3" onClick={() => syncOne(selected.key)} disabled={!!syncing[selected.key]}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${syncing[selected.key] ? 'animate-spin' : ''}`} />{t('rd.syncNow')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DataHub;
